import {inject, Injectable} from '@angular/core';
import {TranslocoService} from '@jsverse/transloco';
import {forkJoin, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {HrCatalogControllerService} from '@frontend/shared-api';
import {
  ArchitecturalPrinciple,
  ArchitecturalPrincipleDirectus,
  DirectusService,
  Module,
} from '@frontend/directus';
import {
  HrToolModuleFunctionGroup,
  HrToolReferenceData,
  HrToolSelectOption,
} from '../components/hr-tool-form-dialog/hr-tool-form.types';

interface TranslatedName {
  language: string;
  name?: string;
}

/** Loads the shared HR-catalog reference data used by both the admin and provider tool forms. */
@Injectable({providedIn: 'root'})
export class HrCatalogReferenceService {
  private readonly catalogApi = inject(HrCatalogControllerService);
  private readonly directusService = inject(DirectusService);
  private readonly translocoService = inject(TranslocoService);

  loadReferenceData(): Observable<HrToolReferenceData> {
    return forkJoin({
      serviceModels: this.catalogApi.getServiceModels(),
      moduleFunctionGroups: this.directusService.loadModuleFunctionsGroupedByModule(),
      countries: this.directusService.loadCountryOptions(),
      languages: this.directusService.loadLanguageOptions(),
      architecturalPrinciples: this.directusService.loadArchitecturalPrinciples(),
      hostingLocations: this.directusService.loadHostingLocations(),
    }).pipe(
      map(
        ({
          serviceModels,
          moduleFunctionGroups,
          countries,
          languages,
          architecturalPrinciples,
          hostingLocations,
        }) => ({
          serviceModels: serviceModels
            .map((model) => ({
              label: this.resolveName(model.translations, model.name),
              value: model.id,
            }))
            .sort(byLabel),
          moduleFunctionGroups: this.toModuleFunctionGroups(moduleFunctionGroups),
          countries: this.toIsoCodeOptions(countries),
          languages: this.toIsoCodeOptions(languages),
          architecturalPrinciples: this.toArchitecturalPrincipleOptions(architecturalPrinciples),
          hostingLocations: hostingLocations
            .map((location) => ({
              label: this.resolveName(location.translations, location.isoCode),
              value: location.isoCode,
            }))
            .sort(byLabel),
        }),
      ),
    );
  }

  imageUrl(fileId: string): string {
    return this.directusService.getHrCatalogImageUrl(fileId);
  }

  /**
   * HR tools use three fixed architectural principles: the base HR Suite without payroll
   * accounting ("suite"), the HR Suite with integrated payroll accounting ("suite_with_payroll"),
   * and the standalone single system ("standalone_system"). The Directus collection also carries
   * combined/other rows for the customer project side, which must not be offered here, so we pin
   * the allowed codes and take their label from the matching single-principle row.
   */
  private toArchitecturalPrincipleOptions(
    principles: ArchitecturalPrincipleDirectus[],
  ): HrToolSelectOption[] {
    const fallbackLabels: Record<ArchitecturalPrinciple, string> = {
      [ArchitecturalPrinciple.SUITE_WITHOUT_PAYROLL]: 'HR Suite without payroll accounting',
      [ArchitecturalPrinciple.SUITE_WITH_PAYROLL]: 'HR Suite with payroll accounting',
      [ArchitecturalPrinciple.STANDALONE_SYSTEM]: 'Standalone System',
    };
    return Object.values(ArchitecturalPrinciple).map((code) => {
      const row = principles.find(
        (principle) => principle.principles.length === 1 && principle.principles[0] === code,
      );
      return {label: this.resolveName(row?.translations, fallbackLabels[code]), value: code};
    });
  }

  private toModuleFunctionGroups(modules: Map<number, Module>): HrToolModuleFunctionGroup[] {
    return Array.from(modules.values())
      .map((module) => ({
        moduleId: module.id,
        label: this.resolveName(module.translations, module.name),
        functions: (module.functions ?? [])
          .map((moduleFunction) => ({
            label: this.resolveName(moduleFunction.translations, moduleFunction.name),
            value: moduleFunction.id,
          }))
          .sort((first, second) => first.label.localeCompare(second.label)),
      }))
      .sort((first, second) => first.label.localeCompare(second.label));
  }

  private toIsoCodeOptions(
    items: {id: string; translations: TranslatedName[]}[],
  ): HrToolSelectOption[] {
    return items
      .map((item) => ({
        label: this.resolveName(item.translations, item.id),
        value: item.id,
      }))
      .sort(byLabel);
  }

  private resolveName(translations: TranslatedName[] | undefined, fallback: string): string {
    if (!translations?.length) {
      return fallback;
    }
    const activeLanguage = this.translocoService.getActiveLang();
    const match = translations.find((translation) => translation.language === activeLanguage);
    return match?.name ?? translations.find((translation) => !!translation.name)?.name ?? fallback;
  }
}

function byLabel(first: HrToolSelectOption, second: HrToolSelectOption): number {
  return first.label.localeCompare(second.label);
}
